/* Vuex module: authentication (Supabase GoTrue via helpers/api). */
import { auth, rest } from 'helpers/api'

const NS = 'auth'
const t = n => `${NS}/${n}`

export const getterTypes = {
  isLoggedIn: t('isLoggedIn'),
  currentUser: t('currentUser'),
  status: t('status'),
  error: t('error'),
  isAdmin: t('isAdmin'),
  mustChangePassword: t('mustChangePassword'),
}

export const actionTypes = {
  login: t('login'),
  logout: t('logout'),
  signup: t('signup'),
  restore: t('restore'),
  resetPassword: t('resetPassword'),
  clearMustChangePassword: t('clearMustChangePassword'),
}

const M = {
  SET_USER: 'SET_USER',
  SET_LOGGED_IN: 'SET_LOGGED_IN',
  SET_STATUS: 'SET_STATUS',
  SET_ERROR: 'SET_ERROR',
  SET_IS_ADMIN: 'SET_IS_ADMIN',
  SET_MUST_CHANGE: 'SET_MUST_CHANGE',
}

export default {
  namespaced: true,

  state: () => ({
    user: null,
    loggedIn: auth.isLoggedIn(),
    status: 'idle', // idle | loading | complete | error
    error: null,
    isAdmin: false,
    mustChangePassword: false,
  }),

  getters: {
    isLoggedIn: s => s.loggedIn,
    currentUser: s => s.user,
    status: s => s.status,
    error: s => s.error,
    isAdmin: s => s.isAdmin,
    mustChangePassword: s => s.mustChangePassword,
  },

  mutations: {
    [M.SET_USER](s, u) {
      s.user = u
    },
    [M.SET_LOGGED_IN](s, v) {
      s.loggedIn = v
    },
    [M.SET_STATUS](s, v) {
      s.status = v
    },
    [M.SET_ERROR](s, e) {
      s.error = e
    },
    [M.SET_IS_ADMIN](s, v) {
      s.isAdmin = v
    },
    [M.SET_MUST_CHANGE](s, v) {
      s.mustChangePassword = v
    },
  },

  actions: {
    async login({ commit }, { email, password }) {
      commit(M.SET_STATUS, 'loading')
      commit(M.SET_ERROR, null)
      try {
        const user = await auth.signIn(email, password)
        commit(M.SET_USER, user)
        commit(M.SET_LOGGED_IN, true)

        let isAdmin = false
        let mustChange = false
        if (user && user.email) {
          try {
            const op = await rest.getMyOperator(user.email)
            if (op && op.role === 'admin' && op.active) {
              isAdmin = true
            }
            if (op && op.must_change_password) {
              mustChange = true
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to resolve operator role:', e)
          }
        }
        commit(M.SET_IS_ADMIN, isAdmin)
        commit(M.SET_MUST_CHANGE, mustChange)

        commit(M.SET_STATUS, 'complete')
        return user
      } catch (err) {
        const msg =
          (err.response &&
            err.response.data &&
            (err.response.data.error_description || err.response.data.msg)) ||
          err.message ||
          'Sign in failed.'
        commit(M.SET_ERROR, msg)
        commit(M.SET_STATUS, 'error')
        throw err
      }
    },

    async logout({ commit }) {
      await auth.signOut()
      commit(M.SET_USER, null)
      commit(M.SET_LOGGED_IN, false)
      commit(M.SET_IS_ADMIN, false)
      commit(M.SET_MUST_CHANGE, false)
      commit(M.SET_STATUS, 'idle')
    },

    async signup({ commit }, { email, password }) {
      commit(M.SET_STATUS, 'loading')
      try {
        const user = await auth.signUp(email, password)
        commit(M.SET_STATUS, 'complete')
        return user
      } catch (err) {
        commit(M.SET_ERROR, err.message)
        commit(M.SET_STATUS, 'error')
        throw err
      }
    },

    async resetPassword(_ctx, email) {
      await auth.sendPasswordReset(email)
    },

    // Called after the user sets their own password: clears the temp-password
    // flag both server-side and in the store so the welcome banner goes away.
    async clearMustChangePassword({ commit, state }) {
      const uid = state.user && state.user.uid
      if (uid) {
        try {
          await rest.clearMustChangePassword(uid)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to clear must_change_password flag:', e)
        }
      }
      commit(M.SET_MUST_CHANGE, false)
    },

    // Called on app boot to rehydrate the session.
    async restore({ commit }) {
      commit(M.SET_STATUS, 'loading')
      try {
        const user = await auth.restore()
        commit(M.SET_USER, user)
        commit(M.SET_LOGGED_IN, !!user)

        let isAdmin = false
        let mustChange = false
        if (user && user.email) {
          try {
            const op = await rest.getMyOperator(user.email)
            if (op && op.role === 'admin' && op.active) {
              isAdmin = true
            }
            if (op && op.must_change_password) {
              mustChange = true
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to resolve operator role on restore:', e)
          }
        }
        commit(M.SET_IS_ADMIN, isAdmin)
        commit(M.SET_MUST_CHANGE, mustChange)
        commit(M.SET_STATUS, 'complete')
        return user
      } catch (err) {
        commit(M.SET_STATUS, 'error')
        throw err
      }
    },
  },
}
